// src/components/molecules/landing/mobile-menu-trigger.tsx
"use client";

import { useState } from "react";
import Link from "next/link";
import { ChevronDown, Menu, X } from "lucide-react";
import { DynamicIcon, type IconName } from "lucide-react/dynamic";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { CtaButton } from "@/components/atoms/landing/cta-button";
import { BrandMark } from "@/components/atoms/landing/brand-mark";
import { isNonRouterHref } from "@/lib/links";
import type { NavItem } from "./header-nav";

type MobileMenuTriggerProps = {
  items: NavItem[];
};

const MOBILE_LINK_CLASSES =
  "min-h-11 flex items-center gap-3 rounded-lg px-3 py-3 text-base font-medium text-brand-ink hover:bg-brand-muted";

/** Icons are authored in WordPress, so the name is only known at runtime. */
function MobileIcon({ name }: { name?: string }) {
  if (!name) return null;
  return (
    <DynamicIcon
      name={name as IconName}
      className="size-5 shrink-0 text-brand"
      aria-hidden
      fallback={() => <span className="size-5 shrink-0" aria-hidden />}
    />
  );
}

/** A link that stays out of the router when the href is external or proxied. */
function MobileLink({
  item,
  onNavigate,
}: {
  item: NavItem;
  onNavigate: () => void;
}) {
  const body = (
    <>
      <MobileIcon name={item.icon} />
      {item.label}
    </>
  );
  if (isNonRouterHref(item.href)) {
    return (
      <a href={item.href} onClick={onNavigate} className={MOBILE_LINK_CLASSES}>
        {body}
      </a>
    );
  }
  return (
    <Link href={item.href} onClick={onNavigate} className={MOBILE_LINK_CLASSES}>
      {body}
    </Link>
  );
}

export function MobileMenuTrigger({ items }: MobileMenuTriggerProps) {
  const [open, setOpen] = useState(false);
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger
        className="inline-flex size-11 items-center justify-center rounded-lg border border-brand-border bg-white text-brand-ink xl:hidden"
        aria-label="Open menu"
      >
        <Menu className="size-5" aria-hidden />
      </SheetTrigger>
      <SheetContent
        side="right"
        className="w-full max-w-sm bg-white p-6 [&>[data-slot=sheet-close-default]]:hidden"
      >
        <SheetHeader className="flex flex-row items-center justify-between p-0">
          <BrandMark width={120} height={24} />
          <SheetTitle className="sr-only">Niftipay navigation</SheetTitle>
          <button
            type="button"
            onClick={() => setOpen(false)}
            aria-label="Close menu"
            className="inline-flex size-11 items-center justify-center rounded-lg text-brand-ink"
          >
            <X className="size-5" aria-hidden />
          </button>
        </SheetHeader>
        <nav aria-label="Primary mobile" className="mt-8">
          <ul className="flex flex-col gap-1">
            {items.map((item) => {
              const key = `${item.label}-${item.href}`;
              const children = item.children ?? [];

              if (!children.length) {
                return (
                  <li key={key}>
                    <MobileLink item={item} onNavigate={() => setOpen(false)} />
                  </li>
                );
              }

              const expanded = expandedKey === key;
              const sectionId = `mobile-nav-${item.label.replace(/[^a-z0-9]+/gi, "-")}`;

              return (
                <li key={key}>
                  <button
                    type="button"
                    aria-expanded={expanded}
                    aria-controls={sectionId}
                    onClick={() => setExpandedKey(expanded ? null : key)}
                    className={`${MOBILE_LINK_CLASSES} w-full justify-between`}
                  >
                    {item.label}
                    <ChevronDown
                      className={`size-5 transition-transform ${expanded ? "rotate-180" : ""}`}
                      aria-hidden
                    />
                  </button>
                  {expanded && (
                    <ul id={sectionId} className="ml-3 border-l border-brand-border pl-2">
                      {item.heading && (
                        <li className="px-3 pt-2 pb-1 text-xs font-semibold uppercase tracking-[0.08em] text-brand-ink/60">
                          {item.heading}
                        </li>
                      )}
                      {children.map((child) => (
                        <li key={`${child.label}-${child.href}`}>
                          <MobileLink item={child} onNavigate={() => setOpen(false)} />
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        </nav>
        <div className="mt-8 flex flex-col gap-3">
          <CtaButton
            href="/login"
            variant="outline"
            size="md"
            trailingIcon={false}
          >
            Sign in
          </CtaButton>
          <CtaButton href="/sign-up" size="md">
            Get started
          </CtaButton>
        </div>
      </SheetContent>
    </Sheet>
  );
}
